import React from "react";
import { Box, Grid } from "@mui/material";
import {
  Inventory2Outlined, CheckCircleOutlineOutlined, BuildOutlined,
  WarningAmberOutlined, EventOutlined, QueryBuilderOutlined,
} from "@mui/icons-material";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import DemoShell from "./DemoShell";
import { MetricCard, ChartCard, DarkTooltip } from "./demoComponents";
import { demo } from "./demoStyles";

// ── Mock data ────────────────────────────────────────────────────────────────
const assetsByCategory = [
  { name: "Solar Pumps",  total: 860, healthy: 792, maintenance: 48, critical: 20 },
  { name: "Solar Panels", total: 580, healthy: 530, maintenance: 36, critical: 14 },
  { name: "Inverters",    total: 420, healthy: 374, maintenance: 30, critical: 16 },
  { name: "Controllers",  total: 180, healthy: 156, maintenance: 18, critical: 6  },
  { name: "Batteries",    total: 100, healthy: 88,  maintenance: 8,  critical: 4  },
];

const maintenanceTrend = [
  { month: "Dec", scheduled: 42, unplanned: 18 },
  { month: "Jan", scheduled: 38, unplanned: 22 },
  { month: "Feb", scheduled: 44, unplanned: 16 },
  { month: "Mar", scheduled: 40, unplanned: 14 },
  { month: "Apr", scheduled: 36, unplanned: 12 },
  { month: "May", scheduled: 34, unplanned: 10 },
];

const healthDist = [
  { name: "Healthy",     value: 1892, color: demo.green  },
  { name: "Maintenance", value: 156,  color: demo.amber  },
  { name: "Critical",    value: 92,   color: "#f87171"   },
];

const recentAssets = [
  { id: "SP-04821", type: "Solar Pump",   location: "Rajasthan",   age: "2.1 yr", health: "Healthy",     nextService: "Jun 2025" },
  { id: "INV-1182", type: "Inverter",     location: "Gujarat",     age: "3.8 yr", health: "Maintenance", nextService: "Now"      },
  { id: "PNL-3341", type: "Solar Panel",  location: "Maharashtra", age: "1.4 yr", health: "Healthy",     nextService: "Sep 2025" },
  { id: "SP-04618", type: "Solar Pump",   location: "Punjab",      age: "4.2 yr", health: "Critical",    nextService: "Overdue"  },
  { id: "CTL-0892", type: "Controller",   location: "Haryana",     age: "2.9 yr", health: "Healthy",     nextService: "Aug 2025" },
];

const GRADIENT = "linear-gradient(135deg, #f472b6, #db2777)";

const healthColor = { Healthy: demo.green, Maintenance: demo.amber, Critical: "#f87171" };

export default function AssetManagementPage() {
  return (
    <DemoShell title="Asset Management" icon={Inventory2Outlined} gradient={GRADIENT}>
      {/* ── KPI row ── */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {[
          { icon: Inventory2Outlined,           label: "Total Assets",       value: "2,140",  sub: "Across all sites",           gradient: GRADIENT },
          { icon: CheckCircleOutlineOutlined,   label: "Healthy",            value: "1,892",  trend: "88.4% fleet health", trendUp: true,  gradient: GRADIENT },
          { icon: BuildOutlined,                label: "Under Maintenance",  value: "156",    sub: "Scheduled & unplanned",      gradient: GRADIENT },
          { icon: WarningAmberOutlined,         label: "Critical",           value: "92",     trend: "-14% vs last month", trendUp: true, gradient: "linear-gradient(135deg, #fb923c, #f87171)" },
          { icon: EventOutlined,                label: "Warranty Expiring",  value: "48",     sub: "Next 90 days",               gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)" },
          { icon: QueryBuilderOutlined,         label: "Avg Asset Age",      value: "3.2",    unit: "yr", sub: "Across fleet",   gradient: "linear-gradient(135deg, #a78bfa, #6366f1)" },
        ].map((m, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <MetricCard {...m} />
          </Grid>
        ))}
      </Grid>

      {/* ── Charts row 1 ── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {/* Stacked bar by category */}
        <Grid item xs={12} lg={8}>
          <ChartCard title="Asset Health by Category" subtitle="Healthy vs Maintenance vs Critical">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={assetsByCategory} margin={{ top: 4, right: 4, bottom: 0, left: -10 }} barSize={32}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: demo.textFaint, fontSize: 10.5 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={({ active, payload, label }) =>
                  active && payload?.length
                    ? <Box sx={{ background: "rgba(12,17,28,0.96)", border: `1px solid ${demo.border}`, borderRadius: "10px", px: 1.8, py: 1.2 }}>
                        <Box sx={{ color: demo.textDim, fontSize: 11.5, mb: 0.8, fontWeight: 600 }}>{label}</Box>
                        {payload.map((p, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: p.fill }} />
                            <span style={{ color: demo.text, fontSize: 12.5 }}>{p.name}: <strong>{p.value}</strong></span>
                          </Box>
                        ))}
                      </Box>
                    : null
                } />
                <Legend formatter={v => <span style={{ color: demo.textDim, fontSize: 12, textTransform: "capitalize" }}>{v}</span>} />
                <Bar dataKey="healthy"     stackId="a" fill={demo.green}  radius={[0,0,0,0]} />
                <Bar dataKey="maintenance" stackId="a" fill={demo.amber}  radius={[0,0,0,0]} />
                <Bar dataKey="critical"    stackId="a" fill="#f87171"     radius={[5,5,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Health distribution donut */}
        <Grid item xs={12} sm={6} lg={4}>
          <ChartCard title="Fleet Health" subtitle="Overall asset status distribution">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={healthDist} cx="50%" cy="50%" innerRadius={62} outerRadius={92}
                  paddingAngle={3} dataKey="value">
                  {healthDist.map((e, i) => <Cell key={i} fill={e.color} stroke="transparent" />)}
                </Pie>
                <Tooltip content={({ active, payload }) =>
                  active && payload?.length
                    ? <Box sx={{ background: "rgba(12,17,28,0.96)", border: `1px solid ${demo.border}`, borderRadius: "8px", px: 1.6, py: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: payload[0].payload.color }} />
                          <span style={{ color: demo.text, fontSize: 13, fontWeight: 700 }}>
                            {payload[0].payload.name}: {payload[0].value.toLocaleString()}
                          </span>
                        </Box>
                      </Box>
                    : null
                } />
                <Legend formatter={(v, e) => <span style={{ color: demo.textDim, fontSize: 12 }}>{e.payload.name} ({e.payload.value})</span>} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* ── Charts row 2 ── */}
      <Grid container spacing={2.5}>
        {/* Maintenance trend */}
        <Grid item xs={12} lg={5}>
          <ChartCard title="Maintenance Events" subtitle="Scheduled vs Unplanned · Last 6 months">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={maintenanceTrend} margin={{ top: 4, right: 4, bottom: 0, left: -15 }} barGap={4}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: demo.textFaint, fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip content={<DarkTooltip unit=" events" />} />
                <Legend formatter={v => <span style={{ color: demo.textDim, fontSize: 12 }}>{v === "scheduled" ? "Scheduled" : "Unplanned"}</span>} />
                <Bar dataKey="scheduled" fill="#a78bfa" radius={[4,4,0,0]} maxBarSize={24} />
                <Bar dataKey="unplanned" fill="#f87171" radius={[4,4,0,0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Asset list */}
        <Grid item xs={12} lg={7}>
          <ChartCard title="Asset Register" subtitle="Recent additions & upcoming service">
            <Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "80px 90px 1fr 55px 80px 85px", gap: "0 10px",
                pb: 1, mb: 0.5, borderBottom: `1px solid ${demo.border}` }}>
                {["Asset ID", "Type", "Location", "Age", "Health", "Service"].map(h => (
                  <Box key={h} sx={{ color: demo.textFaint, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</Box>
                ))}
              </Box>
              {recentAssets.map((a, i) => (
                <Box key={i} sx={{ display: "grid", gridTemplateColumns: "80px 90px 1fr 55px 80px 85px", gap: "0 10px",
                  py: 1.2, borderBottom: `1px solid ${demo.border}`, "&:last-child": { borderBottom: "none" }, alignItems: "center" }}>
                  <Box sx={{ color: demo.accent, fontSize: 12, fontWeight: 700 }}>{a.id}</Box>
                  <Box sx={{ color: demo.text, fontSize: 12.5 }}>{a.type}</Box>
                  <Box sx={{ color: demo.textDim, fontSize: 12.5 }}>{a.location}</Box>
                  <Box sx={{ color: demo.textDim, fontSize: 12 }}>{a.age}</Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: healthColor[a.health] }} />
                    <Box sx={{ color: healthColor[a.health], fontSize: 12, fontWeight: 600 }}>{a.health}</Box>
                  </Box>
                  <Box sx={{ color: a.nextService === "Overdue" ? "#f87171" : a.nextService === "Now" ? demo.amber : demo.textDim, fontSize: 12, fontWeight: a.nextService === "Overdue" || a.nextService === "Now" ? 700 : 400 }}>
                    {a.nextService}
                  </Box>
                </Box>
              ))}
            </Box>
          </ChartCard>
        </Grid>
      </Grid>
    </DemoShell>
  );
}
